import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { useLeagueSeasonStore } from "@/state/useLeagueSeasonStore";
import { isSuperAdmin } from "@/lib/auth/isSuperAdmin";
import {
  ALL_MODULES_ENABLED,
  resolveModules,
  type LeagueModulesConfig,
  type ResolvedModules,
} from "@/lib/modules/registry";
import { supabase } from "@/lib/supabaseClient";

/**
 * Modules activés pour la ligue actuellement sélectionnée.
 * - super_admin : tout activé (bypass, ne peut jamais se verrouiller dehors) ;
 * - chargement ou erreur : tout activé (fail open — c'est un gate UX, pas une
 *   frontière de sécurité) ;
 * - sinon : résolution de league_settings_versions.config.modules (absence = activé).
 */
export function useLeagueModules(): {
  modules: ResolvedModules;
  isLoading: boolean;
} {
  const { user } = useAuth();
  const currentLeagueId = useLeagueSeasonStore((s) => s.currentLeagueId);
  const superAdmin = isSuperAdmin(user);

  const { data, isLoading } = useQuery({
    queryKey: ["league-modules", currentLeagueId],
    enabled: !!currentLeagueId && !superAdmin,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<LeagueModulesConfig | null> => {
      const { data: row, error } = await supabase
        .from("league_settings_versions")
        .select("config")
        .eq("league_id", currentLeagueId)
        .eq("is_active", true)
        // Aucune contrainte d'unicité sur (league_id, is_active) : en cas de
        // doublon, la version la plus récente l'emporte plutôt qu'une erreur.
        .order("version", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      const config = (row as { config?: { modules?: LeagueModulesConfig } | null } | null)?.config;
      return config?.modules ?? null;
    },
  });

  // Identité stable : les consommateurs mettent `modules` dans des tableaux de
  // dépendances, un nouvel objet à chaque rendu relancerait leurs effets.
  const modules = useMemo(() => resolveModules(data), [data]);

  if (superAdmin) return { modules: ALL_MODULES_ENABLED, isLoading: false };
  if (isLoading) return { modules: ALL_MODULES_ENABLED, isLoading: true };
  return { modules, isLoading: false };
}
